import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { API } from '../App';
import { useCluster } from '../context/ClusterContext';

// Realizable Savings (roadmap Phase −1, buyer-validation MVP).
// Answers the one question a cold buyer asks: "If I apply these
// recommendations, how much can I ACTUALLY realize — and what's blocking
// the rest?" The "Why not more?" table is the hero — the explanation is the
// product. Backed by GET /api/v2/clusters/{id}/realizable-savings, which is
// gated by OPTOPS_REALIZABLE_SAVINGS on the backend.

const fmt = (v) =>
  typeof v === 'number'
    ? v.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
    : '—';

// CAST-style available-savings scenarios. The labels/order the cards render in;
// keys map to the backend `scenarios` map (available_savings.compute_available_savings).
const SCENARIO_META = [
  { key: 'optimized_on_demand', label: 'Optimized on-demand', accent: 'emerald' },
  { key: 'optimized_spot_friendly', label: 'Spot-friendly', accent: 'emerald' },
  { key: 'all_spot', label: 'All-spot', accent: 'amber' },
];

// Collapse a scenario's proposedNodes [{instanceType,count,spot}] into a short,
// human label like "3× m5.large · 1× t3.medium (spot)". Defensive: a cold-start
// fallback may omit proposedNodes entirely.
const proposedMix = (nodes) =>
  Array.isArray(nodes) && nodes.length
    ? nodes
        .map((n) => `${n.count ?? '?'}× ${n.instanceType || '—'}${n.spot ? ' (spot)' : ''}`)
        .join(' · ')
    : '—';

const totalProposedNodes = (nodes) =>
  Array.isArray(nodes) ? nodes.reduce((s, n) => s + (Number(n.count) || 0), 0) : 0;

function KpiTile({ label, value, sub, accent }) {
  return (
    <div className="bg-white dark:bg-slate-800 p-4 rounded-md border border-slate-200 dark:border-slate-700">
      <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={`text-3xl font-semibold mt-1 tabular-nums ${accent || 'text-slate-900 dark:text-slate-100'}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function RealizableSavings() {
  const { selectedCluster } = useCluster();
  const clusterId = selectedCluster?.id;

  const [data, setData] = useState(null);
  const [breakdown, setBreakdown] = useState(null);   // staged headline (rightsize+consolidate+spot)
  const [available, setAvailable] = useState(null);   // CAST-style available-savings scenarios
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const loadData = useCallback(async () => {
    if (!clusterId) return;
    setLoading(true);
    setErrorMsg('');
    try {
      // available-savings is an independent, read-only endpoint — don't let a
      // failure there block the realizable-savings hero (it may be flag-off).
      const [rs, st, av] = await Promise.all([
        axios.get(`${API}/v2/clusters/${clusterId}/realizable-savings`),
        axios.get(`${API}/dashboard/stats?cluster_id=${clusterId}`).catch(() => null),
        axios.get(`${API}/v2/clusters/${clusterId}/available-savings`).catch(() => null),
      ]);
      setData(rs.data);
      setBreakdown(st?.data?.savings_breakdown || null);
      setAvailable(av?.data || null);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'failed to load';
      setErrorMsg(msg);
      toast.error(`Failed to load realizable savings: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [clusterId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const noData = data && data.status === 'no_data';
  const ready = data && data.status === 'ok';
  const realizationPct = ready ? data.realizable?.realization_pct ?? 0 : 0;

  // Available-savings (CAST-style scenarios) — independent of the realizable
  // hero above. Render only when the endpoint returned a priced snapshot.
  const avReady = available && available.status === 'ok';
  const avCurrent = avReady ? available.currentConfiguration || {} : {};
  const avScenarios = avReady ? available.scenarios || {} : {};

  return (
    <Layout>
      <div className="px-6 py-5 space-y-6">
        {/* Header */}
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
              Realizable Savings
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              How much of the headline savings you can actually realize — and what&apos;s blocking the rest.
            </p>
          </div>
          <button
            onClick={loadData}
            className="px-3 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
            disabled={loading || !clusterId}
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {!clusterId && (
          <div className="text-sm text-slate-500">Select a cluster to see realizable savings.</div>
        )}

        {errorMsg && <div className="text-sm text-red-600">{errorMsg}</div>}

        {noData && (
          <div className="text-sm text-slate-500">
            No node-state snapshot yet — the agent may not be reporting.
          </div>
        )}

        {/* Staged headline + waterfall (rightsize → consolidate → optimise survivors) */}
        {breakdown && breakdown.confidence === 'ok' && (
          <div className="bg-white dark:bg-slate-800 rounded-md border border-slate-200 dark:border-slate-700 p-4">
            <div className="flex items-baseline justify-between">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                We can save you ~{fmt(breakdown.approx_total_monthly)}/mo more
                {breakdown.realized_monthly > 0 && (
                  <span className="ml-2 text-xs font-normal text-slate-500">
                    already saving {fmt(breakdown.realized_monthly)}/mo
                  </span>
                )}
              </h2>
              <span className="text-xs text-slate-500">additional opportunity · realized shown separately</span>
            </div>
            {(() => {
              const base = breakdown.baseline_monthly || 0;
              const cons = breakdown.categories?.consolidation?.monthly || 0;
              const io = breakdown.categories?.instance_optimization?.monthly || 0;
              const steps = [
                { label: 'Current node cost', val: base, kind: 'base' },
                { label: 'after consolidation', val: -cons, kind: 'save' },
                { label: 'after spot / instance-type', val: -io, kind: 'save' },
                { label: 'Realizable (≈ remaining)', val: Math.max(0, base - cons - io), kind: 'end' },
              ];
              const pct = (v) => (base > 0 ? Math.max(2, Math.min(100, (Math.abs(v) / base) * 100)) : 0);
              return (
                <div className="mt-3 space-y-1.5">
                  {steps.map((s, i) => (
                    <div key={i} className="flex items-center gap-3 text-xs">
                      <div className="w-44 text-right text-slate-500">{s.label}</div>
                      <div className="flex-1 h-4 rounded bg-slate-100 dark:bg-slate-700 overflow-hidden">
                        <div
                          className={`h-full rounded ${s.kind === 'save' ? 'bg-emerald-500' : s.kind === 'end' ? 'bg-primary' : 'bg-slate-400'}`}
                          style={{ width: `${pct(s.val)}%` }}
                        />
                      </div>
                      <div className={`w-20 tabular-nums ${s.kind === 'save' ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-700 dark:text-slate-200'}`}>
                        {s.kind === 'save' ? `−${fmt(Math.abs(s.val))}` : fmt(s.val)}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <KpiTile label="Consolidation" value={fmt(breakdown.categories?.consolidation?.monthly)} sub="fewer nodes (node-cost)" accent="text-emerald-600 dark:text-emerald-400" />
              <KpiTile label="Spot / instance-type" value={fmt(breakdown.categories?.instance_optimization?.monthly)} sub="on surviving nodes" accent="text-emerald-600 dark:text-emerald-400" />
              <KpiTile label="Rightsizing (enables the above)" value={fmt(breakdown.categories?.rightsizing_enabler?.request_priced_monthly)} sub="request-priced · not added to total" accent="text-slate-500 dark:text-slate-400" />
            </div>
          </div>
        )}

        {ready && (
          <>
            {/* KPI row: current cost, potential, realizable */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <KpiTile
                label="Current monthly cost"
                value={fmt(data.current_monthly_cost)}
                sub={`${data.node_count} nodes`}
              />
              <KpiTile
                label="Potential savings"
                value={fmt(data.potential?.monthly)}
                sub={`${data.potential?.nodes_removable ?? 0} nodes removable, unconstrained`}
                accent="text-slate-500 dark:text-slate-400"
              />
              <KpiTile
                label="Realizable savings"
                value={fmt(data.realizable?.monthly)}
                sub={`${data.realizable?.nodes_removable ?? 0} nodes you can actually drain`}
                accent="text-emerald-600 dark:text-emerald-400"
              />
            </div>

            {/* Realization bar — the gap between potential and realizable. */}
            <div className="bg-white dark:bg-slate-800 p-4 rounded-md border border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-600 dark:text-slate-300">
                  Realizable is{' '}
                  <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                    {realizationPct}%
                  </span>{' '}
                  of potential
                </span>
                <span className="text-xs text-slate-500 tabular-nums">
                  {fmt(data.realizable?.monthly)} of {fmt(data.potential?.monthly)} / mo
                </span>
              </div>
              <div className="h-2.5 w-full rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                <div
                  className="h-full rounded-full bg-emerald-500"
                  style={{ width: `${Math.min(100, Math.max(0, realizationPct))}%` }}
                />
              </div>
            </div>

            {/* Why not more? — the hero. */}
            <div className="bg-white dark:bg-slate-800 rounded-md border border-slate-200 dark:border-slate-700">
              <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
                <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Why not more?
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Savings blocked by constraints we can&apos;t safely override — each with a fix.
                </p>
              </div>
              {(!data.why_not_more || data.why_not_more.length === 0) ? (
                <div className="px-4 py-4 text-sm text-slate-500">
                  Nothing blocking — all removable nodes are drainable.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-slate-500 text-xs uppercase">
                    <tr>
                      <th className="text-left px-4 py-2">Constraint</th>
                      <th className="text-right px-4 py-2">Blocked / mo</th>
                      <th className="text-right px-4 py-2">Nodes</th>
                      <th className="text-left px-4 py-2">Example nodes</th>
                      <th className="text-left px-4 py-2">Fix</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.why_not_more.map((row) => (
                      <tr
                        key={row.constraint}
                        className="border-t border-slate-100 dark:border-slate-700 align-top"
                      >
                        <td className="px-4 py-2">
                          <span className="text-slate-900 dark:text-slate-100">{row.label}</span>
                          <span className="ml-2 text-[10px] uppercase tracking-wide bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 px-1.5 py-0.5 rounded">
                            {row.constraint}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-amber-700 dark:text-amber-300">
                          {fmt(row.blocked_monthly)}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">{row.blocked_nodes}</td>
                        <td className="px-4 py-2 font-mono text-xs text-slate-500">
                          {(row.example_nodes || []).join(', ')}
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-600 dark:text-slate-300">
                          {row.fix}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Honesty footnote — realizable is an upper bound. */}
            {data.assumptions && (
              <details className="text-xs text-slate-500">
                <summary className="cursor-pointer select-none">How this is calculated</summary>
                <ul className="list-disc ml-5 mt-2 space-y-1">
                  {data.assumptions.map((a, i) => (
                    <li key={i}>{a}</li>
                  ))}
                </ul>
              </details>
            )}
          </>
        )}

        {/* CAST-style available-savings scenarios — "if OptOps ran this
            cluster, what would it cost?" Computed from the first node-state
            snapshot, independent of the realizable hero above. */}
        {avReady && (
          <div className="space-y-4">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div>
                <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Savings scenarios
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Re-bin-packing today&apos;s workload onto the cheapest instance mix —
                  on-demand, spot-friendly, or all-spot. Public list prices, no cloud credentials.
                </p>
              </div>
              {available.cloudProvider && (
                <span className="text-[10px] uppercase tracking-wide bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded">
                  {available.cloudProvider}
                </span>
              )}
            </div>

            {/* Scenario comparison cards: Current + each scenario. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {/* Current configuration — the "before" baseline. */}
              <div className="bg-white dark:bg-slate-800 p-4 rounded-md border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-900 dark:text-slate-100">
                    Current
                  </span>
                  <span className="text-[10px] uppercase tracking-wide bg-slate-100 dark:bg-slate-700 text-slate-500 px-1.5 py-0.5 rounded">
                    baseline
                  </span>
                </div>
                <div className="text-2xl font-semibold mt-2 tabular-nums text-slate-900 dark:text-slate-100">
                  {fmt(avCurrent.totalMonthly)}
                  <span className="text-xs font-normal text-slate-500"> /mo</span>
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  {avCurrent.nodeCount ?? 0} nodes
                  {typeof avCurrent.spotNodeCount === 'number' && avCurrent.spotNodeCount > 0
                    ? ` · ${avCurrent.spotNodeCount} spot`
                    : ''}
                </div>
                {typeof available.cpuWastePercent === 'number' && (
                  <div className="text-xs text-slate-400 mt-1">
                    {available.cpuWastePercent}% CPU unrequested
                  </div>
                )}
              </div>

              {SCENARIO_META.map(({ key, label, accent }) => {
                const s = avScenarios[key];
                if (!s) return null;
                const before = s.priceBefore?.monthly;
                const after = s.priceAfter?.monthly;
                const savedMonthly =
                  typeof before === 'number' && typeof after === 'number'
                    ? Math.max(0, before - after)
                    : null;
                const pct = typeof s.savingsPercent === 'number' ? s.savingsPercent : null;
                const noFit = !s.proposedNodes || s.proposedNodes.length === 0;
                return (
                  <div
                    key={key}
                    className="bg-white dark:bg-slate-800 p-4 rounded-md border border-slate-200 dark:border-slate-700"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-900 dark:text-slate-100">
                        {label}
                      </span>
                      {pct !== null && (
                        <span
                          className={`text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded ${
                            accent === 'amber'
                              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                              : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                          }`}
                        >
                          −{pct}%
                        </span>
                      )}
                    </div>
                    <div
                      className={`text-2xl font-semibold mt-2 tabular-nums ${
                        accent === 'amber'
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-emerald-600 dark:text-emerald-400'
                      }`}
                    >
                      {fmt(after)}
                      <span className="text-xs font-normal text-slate-500"> /mo</span>
                    </div>
                    {savedMonthly !== null && (
                      <div className="text-xs text-slate-500 mt-1 tabular-nums">
                        save {fmt(savedMonthly)}/mo from {fmt(before)}
                      </div>
                    )}
                    <div className="text-xs text-slate-500 mt-2">
                      {noFit ? 'no proposal' : `${totalProposedNodes(s.proposedNodes)} nodes`}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1 font-mono break-words">
                      {proposedMix(s.proposedNodes)}
                    </div>
                    {s.capped && (
                      <div className="text-[10px] text-amber-600 dark:text-amber-400 mt-1.5">
                        capped at 60% (raw {s.uncappedSavingsPercent}%)
                      </div>
                    )}
                    {s.note && (
                      <div className="text-[10px] text-slate-400 mt-1.5">{s.note}</div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Why not more? — constraints blocking further savings (the
                available-savings table; distinct from the realizable one above). */}
            {Array.isArray(available.constraints) && available.constraints.length > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-md border border-slate-200 dark:border-slate-700">
                <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    Why not more? (scenarios)
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Constraints that keep workloads off spot or set a per-node floor.
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-slate-500 text-xs uppercase">
                      <tr>
                        <th className="text-left px-4 py-2">Constraint</th>
                        <th className="text-right px-4 py-2">Pods</th>
                        <th className="text-left px-4 py-2">Example workloads</th>
                        <th className="text-left px-4 py-2">Fix</th>
                      </tr>
                    </thead>
                    <tbody>
                      {available.constraints.map((row, i) => (
                        <tr
                          key={row.constraint || i}
                          className="border-t border-slate-100 dark:border-slate-700 align-top"
                        >
                          <td className="px-4 py-2">
                            <span className="text-slate-900 dark:text-slate-100">
                              {row.label || row.constraint}
                            </span>
                            {row.constraint && (
                              <span className="ml-2 text-[10px] uppercase tracking-wide bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 px-1.5 py-0.5 rounded">
                                {row.constraint}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">{row.pods ?? '—'}</td>
                          <td className="px-4 py-2 font-mono text-xs text-slate-500">
                            {(row.example_workloads || []).join(', ') ||
                              (typeof row.cpu_cores_per_node === 'number'
                                ? `${row.cpu_cores_per_node} vCPU · ${row.memory_gib_per_node} GiB / node`
                                : '—')}
                          </td>
                          <td className="px-4 py-2 text-xs text-slate-600 dark:text-slate-300">
                            {row.fix || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Scenario assumptions — separate from the realizable footnote. */}
            {Array.isArray(available.assumptions) && available.assumptions.length > 0 && (
              <details className="text-xs text-slate-500">
                <summary className="cursor-pointer select-none">
                  How these scenarios are calculated
                </summary>
                <ul className="list-disc ml-5 mt-2 space-y-1">
                  {available.assumptions.map((a, i) => (
                    <li key={i}>{a}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
